import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Globe, 
  MapPin, 
  FileText, 
  Zap, 
  Shield, 
  Users, 
  HeadphonesIcon,
  Search,
  PenTool,
  Rocket,
  ChevronRight,
  Menu,
  X,
  Mail,
  Phone,
  ArrowRight,
  Check,
  Linkedin,
  Instagram,
  Facebook,
  Send,
  Code,
  Monitor,
  Smartphone,
  Layout,
  Star,
  Target,
  TrendingUp,
  Palette,
  Layers
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Hook for scroll-based animation progress
const useScrollProgress = () => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollY(window.scrollY);
      setScrollProgress(window.scrollY / totalHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { scrollY, scrollProgress };
};

// Scroll Progress Component
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = (window.scrollY / totalHeight) * 100;
      setProgress(scrollProgress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return <div className="scroll-progress" style={{ width: `${progress}%` }} />;
};

// Interstitial Animation Section - Laptop Opening
const LaptopOpeningSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.3);
  const mouse = useContext(MouseContext);
  
  return (
    <section 
      ref={sectionRef}
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1117 50%, #0a0a0a 100%)' }}
    >
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Text */}
          <div className={`text-center lg:text-left max-w-md scroll-fade-in ${isVisible ? 'visible' : ''}`}>
            <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-2 block">
              Come lavoriamo
            </span>
            <h3 className="text-2xl lg:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Apriamo il tuo progetto
            </h3>
            <p className="text-white/60">
              Ogni collaborazione inizia con l'analisi delle tue esigenze. 
              Accendiamo il computer e iniziamo a costruire.
            </p>
          </div>
          
          {/* Laptop Animation - Opening */}
          <div 
            className={`relative scroll-scale ${isVisible ? 'visible' : ''}`}
            style={{
              perspective: '1500px',
              transform: `translate(${mouse.x * 10}px, ${mouse.y * 10}px)`
            }}
          >
            {/* Glow */}
            <div 
              className="absolute inset-0 blur-[80px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(14, 165, 233, 0.3) 0%, transparent 70%)',
                transform: 'scale(1.5)',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 1s ease'
              }}
            />
            
            {/* Laptop */}
            <div 
              className="relative"
              style={{
                transformStyle: 'preserve-3d',
                transform: `rotateX(${5 + mouse.y * 3}deg) rotateY(${mouse.x * 5}deg)`
              }}
            >
              {/* Base */}
              <div 
                className="w-[280px] h-[16px] rounded-b-lg mx-auto"
                style={{ background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)' }}
              >
                <div className="absolute top-[-6px] left-1/2 -translate-x-1/2 w-[240px] h-[6px] rounded-t bg-gradient-to-b from-[#333] to-[#2a2a2a]" />
              </div>
              
              {/* Screen - Opening animation */}
              <div 
                className="w-[280px] h-[175px] rounded-t-lg mx-auto origin-bottom"
                style={{
                  background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)',
                  transform: `rotateX(${isVisible ? 0 : -70}deg)`,
                  transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  transitionDelay: '0.3s',
                  boxShadow: isVisible ? '0 0 40px rgba(14, 165, 233, 0.2)' : 'none'
                }}
              >
                <div className="w-full h-full p-2 flex items-center justify-center">
                  <div 
                    className="text-[#0ea5e9] text-center"
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transition: 'opacity 0.5s ease',
                      transitionDelay: '1.5s'
                    }}
                  >
                    <Monitor size={40} className="mx-auto mb-2" />
                    <span className="text-xs">Pronto per iniziare</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Interstitial Animation Section - Browser Search
const BrowserSearchSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.3);
  const [typedText, setTypedText] = useState('');
  const fullText = 'mm-webdesigners.it';
  
  useEffect(() => {
    if (isVisible) {
      let index = 0;
      const timer = setInterval(() => {
        if (index <= fullText.length) {
          setTypedText(fullText.slice(0, index));
          index++;
        } else {
          clearInterval(timer);
        }
      }, 100);
      return () => clearInterval(timer);
    }
  }, [isVisible]);
  
  return (
    <section 
      ref={sectionRef}
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1117 50%, #0a0a0a 100%)' }}
    >
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row-reverse items-center justify-center gap-12">
          {/* Text */}
          <div className={`text-center lg:text-left max-w-md scroll-fade-in ${isVisible ? 'visible' : ''}`}>
            <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-2 block">
              Il tuo sito prende forma
            </span>
            <h3 className="text-2xl lg:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Digitiamo il tuo dominio
            </h3>
            <p className="text-white/60">
              Sviluppiamo il tuo sito e lo rendiamo accessibile online. 
              La tua presenza digitale sta per nascere.
            </p>
          </div>
          
          {/* Browser Animation */}
          <div className={`scroll-scale ${isVisible ? 'visible' : ''}`}>
            <div 
              className="w-[320px] rounded-lg overflow-hidden"
              style={{
                background: '#1a1a1a',
                boxShadow: isVisible ? '0 0 60px rgba(14, 165, 233, 0.2)' : 'none',
                transition: 'box-shadow 1s ease'
              }}
            >
              {/* Browser Chrome */}
              <div className="h-[32px] flex items-center px-3 gap-2 bg-[#252525]">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                
                {/* URL Bar */}
                <div className="flex-1 mx-2 h-[22px] rounded bg-[#333] flex items-center px-3">
                  <span className="text-sm text-[#0ea5e9] font-mono">
                    {typedText}
                  </span>
                  <span 
                    className="w-[2px] h-4 bg-[#0ea5e9] ml-0.5"
                    style={{ animation: 'pulse 1s infinite' }}
                  />
                </div>
              </div>
              
              {/* Browser Content - Loading */}
              <div className="h-[180px] flex items-center justify-center bg-[#111]">
                <div 
                  className="text-center"
                  style={{
                    opacity: typedText === fullText ? 1 : 0,
                    transition: 'opacity 0.5s ease'
                  }}
                >
                  <div 
                    className="w-10 h-10 border-2 border-[#0ea5e9] border-t-transparent rounded-full mx-auto mb-3"
                    style={{ animation: 'spin 1s linear infinite' }}
                  />
                  <span className="text-white/50 text-sm">Caricamento...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Interstitial Animation Section - Website Ready
const WebsiteReadySection = () => {
  const [sectionRef, isVisible] = useScrollAnimation(0.3);
  const mouse = useContext(MouseContext);
  
  return (
    <section 
      ref={sectionRef}
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1117 50%, #0a0a0a 100%)' }}
    >
      <div className="container-custom">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Text */}
          <div className={`text-center lg:text-left max-w-md scroll-fade-in ${isVisible ? 'visible' : ''}`}>
            <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-2 block">
              Risultato finale
            </span>
            <h3 className="text-2xl lg:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Il tuo sito è online!
            </h3>
            <p className="text-white/60">
              Un sito professionale, veloce e ottimizzato. Pronto per convertire 
              visitatori in clienti e far crescere il tuo business.
            </p>
          </div>
          
          {/* Final Website Preview */}
          <div 
            className={`scroll-scale ${isVisible ? 'visible' : ''}`}
            style={{
              transform: `translate(${mouse.x * 8}px, ${mouse.y * 8}px)`
            }}
          >
            {/* Glow */}
            <div 
              className="absolute inset-0 blur-[100px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(14, 165, 233, 0.4) 0%, transparent 70%)',
                transform: 'scale(1.5)',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 1s ease 0.5s'
              }}
            />
            
            <div 
              className="relative w-[340px] rounded-lg overflow-hidden"
              style={{
                background: '#0a0a0a',
                boxShadow: isVisible ? '0 0 80px rgba(14, 165, 233, 0.3), 0 20px 60px rgba(0,0,0,0.5)' : 'none',
                transition: 'box-shadow 1s ease',
                border: '1px solid rgba(14, 165, 233, 0.2)'
              }}
            >
              {/* Browser Chrome */}
              <div className="h-[28px] flex items-center px-3 gap-2 bg-[#1a1a1a] border-b border-white/5">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                </div>
                <div className="flex-1 mx-2 h-[18px] rounded bg-[#252525] flex items-center px-2">
                  <span className="text-[10px] text-[#0ea5e9] font-mono">mm-webdesigners.it</span>
                </div>
              </div>
              
              {/* Mini Website */}
              <div 
                className="p-4 space-y-3"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: `translateY(${isVisible ? 0 : 20}px)`,
                  transition: 'all 0.8s ease 0.5s'
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-between pb-2 border-b border-white/10">
                  <div className="w-8 h-4 bg-gradient-to-r from-[#0ea5e9] to-[#c0c0c0] rounded" />
                  <div className="flex gap-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="w-8 h-2 bg-white/20 rounded" />
                    ))}
                  </div>
                </div>
                
                {/* Hero */}
                <div className="py-3">
                  <div className="w-32 h-3 bg-white rounded mb-1" />
                  <div className="w-24 h-2.5 bg-[#0ea5e9] rounded mb-3" />
                  <div className="w-full h-1.5 bg-white/20 rounded mb-1" />
                  <div className="w-3/4 h-1.5 bg-white/20 rounded mb-3" />
                  <div className="flex gap-2">
                    <div className="w-16 h-5 bg-[#0ea5e9] rounded-full" />
                    <div className="w-14 h-5 border border-[#0ea5e9] rounded-full" />
                  </div>
                </div>
                
                {/* Stats */}
                <div className="flex gap-3 py-2 border-t border-white/10">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex-1 text-center">
                      <div className="w-6 h-3 bg-[#0ea5e9] rounded mx-auto mb-1" />
                      <div className="w-full h-1 bg-white/10 rounded" />
                    </div>
                  ))}
                </div>
                
                {/* Team */}
                <div className="flex justify-center gap-2 pt-2">
                  {[...Array(3)].map((_, i) => (
                    <div 
                      key={i} 
                      className="w-8 h-8 rounded-full border-2 border-[#0ea5e9]"
                      style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 3D Cube Component for Hero
const Hero3DCube = () => {
  const mouse = useContext(MouseContext);
  
  return (
    <div 
      className="hidden lg:block absolute right-[10%] top-1/2 -translate-y-1/2 perspective-1000"
      style={{
        transform: `translate(${mouse.x * 30}px, ${mouse.y * 30}px)`
      }}
    >
      <div className="cube-3d hero-3d-glow">
        <div className="cube-face cube-face-front">
          <Code size={32} className="text-[#0ea5e9]" />
        </div>
        <div className="cube-face cube-face-back">
          <Globe size={32} className="text-[#0ea5e9]" />
        </div>
        <div className="cube-face cube-face-left">
          <Monitor size={32} className="text-[#0ea5e9]" />
        </div>
        <div className="cube-face cube-face-right">
          <Smartphone size={32} className="text-[#0ea5e9]" />
        </div>
        <div className="cube-face cube-face-top">
          <Layout size={32} className="text-[#0ea5e9]" />
        </div>
        <div className="cube-face cube-face-bottom">
          <MapPin size={32} className="text-[#0ea5e9]" />
        </div>
      </div>
    </div>
  );
};

// Mouse Position Context for 3D effects
const MouseContext = createContext({ x: 0, y: 0 });

const MouseProvider = ({ children }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <MouseContext.Provider value={mousePos}>
      {children}
    </MouseContext.Provider>
  );
};

// Floating 3D Elements Component
const Floating3DElements = ({ variant = "default" }) => {
  const mouse = useContext(MouseContext);
  
  const elements = {
    hero: [
      { icon: <Code size={40} />, x: 8, y: 15, speed: 0.3, delay: 0, color: "#0ea5e9" },
      { icon: <Monitor size={36} />, x: 88, y: 12, speed: 0.4, delay: 0.5, color: "#c0c0c0" },
      { icon: <Globe size={44} />, x: 92, y: 65, speed: 0.25, delay: 1, color: "#0ea5e9" },
      { icon: <Smartphone size={32} />, x: 3, y: 70, speed: 0.35, delay: 1.5, color: "#c0c0c0" },
      { icon: <Layout size={38} />, x: 78, y: 35, speed: 0.3, delay: 2, color: "#0ea5e9" },
      { icon: <MapPin size={34} />, x: 12, y: 45, speed: 0.4, delay: 2.5, color: "#c0c0c0" },
      { icon: <Star size={28} />, x: 95, y: 85, speed: 0.35, delay: 3, color: "#0ea5e9" },
      { icon: <Target size={30} />, x: 5, y: 90, speed: 0.3, delay: 3.5, color: "#c0c0c0" },
    ],
    services: [
      { icon: <Code size={32} />, x: 3, y: 8, speed: 0.3, delay: 0, color: "#0ea5e9" },
      { icon: <Star size={28} />, x: 97, y: 15, speed: 0.4, delay: 0.3, color: "#c0c0c0" },
      { icon: <Target size={30} />, x: 5, y: 85, speed: 0.35, delay: 0.6, color: "#0ea5e9" },
      { icon: <TrendingUp size={34} />, x: 95, y: 80, speed: 0.3, delay: 0.9, color: "#c0c0c0" },
      { icon: <Globe size={26} />, x: 92, y: 45, speed: 0.35, delay: 1.2, color: "#0ea5e9" },
      { icon: <Palette size={28} />, x: 8, y: 50, speed: 0.4, delay: 1.5, color: "#c0c0c0" },
    ],
    process: [
      { icon: <Search size={32} />, x: 3, y: 12, speed: 0.3, delay: 0, color: "#0ea5e9" },
      { icon: <PenTool size={30} />, x: 97, y: 20, speed: 0.4, delay: 0.4, color: "#c0c0c0" },
      { icon: <Rocket size={34} />, x: 95, y: 75, speed: 0.35, delay: 0.8, color: "#0ea5e9" },
      { icon: <Code size={28} />, x: 5, y: 80, speed: 0.3, delay: 1.2, color: "#c0c0c0" },
      { icon: <Zap size={26} />, x: 92, y: 50, speed: 0.35, delay: 1.6, color: "#0ea5e9" },
    ],
    portfolio: [
      { icon: <Palette size={32} />, x: 3, y: 15, speed: 0.3, delay: 0, color: "#0ea5e9" },
      { icon: <Monitor size={30} />, x: 97, y: 12, speed: 0.4, delay: 0.3, color: "#c0c0c0" },
      { icon: <Globe size={28} />, x: 5, y: 85, speed: 0.35, delay: 0.6, color: "#0ea5e9" },
      { icon: <FileText size={30} />, x: 95, y: 80, speed: 0.3, delay: 0.9, color: "#c0c0c0" },
      { icon: <Layout size={26} />, x: 92, y: 45, speed: 0.35, delay: 1.2, color: "#0ea5e9" },
      { icon: <Star size={24} />, x: 8, y: 50, speed: 0.4, delay: 1.5, color: "#c0c0c0" },
    ]
  };

  const currentElements = elements[variant] || elements.hero;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {currentElements.map((el, i) => (
        <div
          key={i}
          className="absolute transition-transform duration-700 ease-out"
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`,
            transform: `translate(${mouse.x * el.speed * 50}px, ${mouse.y * el.speed * 50}px)`,
            color: el.color,
            opacity: 0.25,
            animationDelay: `${el.delay}s`
          }}
        >
          <div className="animate-float" style={{ animationDelay: `${el.delay}s` }}>
            {el.icon}
          </div>
        </div>
      ))}
      
      {/* Gradient orbs */}
      <div 
        className="absolute w-[600px] h-[600px] rounded-full opacity-30 blur-[120px] transition-transform duration-1000"
        style={{
          background: 'radial-gradient(circle, #0ea5e9 0%, transparent 70%)',
          left: '55%',
          top: '15%',
          transform: `translate(${mouse.x * 40}px, ${mouse.y * 40}px)`
        }}
      />
      <div 
        className="absolute w-[500px] h-[500px] rounded-full opacity-15 blur-[100px] transition-transform duration-1000"
        style={{
          background: 'radial-gradient(circle, #c0c0c0 0%, transparent 70%)',
          left: '5%',
          top: '55%',
          transform: `translate(${mouse.x * -30}px, ${mouse.y * -30}px)`
        }}
      />
      <div 
        className="absolute w-[400px] h-[400px] rounded-full opacity-20 blur-[80px] transition-transform duration-1000"
        style={{
          background: 'radial-gradient(circle, #0ea5e9 0%, transparent 70%)',
          right: '10%',
          bottom: '10%',
          transform: `translate(${mouse.x * 25}px, ${mouse.y * 25}px)`
        }}
      />
    </div>
  );
};

// Grid Background Component
const GridBackground = () => {
  const mouse = useContext(MouseContext);
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Animated grid */}
      <div 
        className="absolute inset-0 transition-transform duration-1000"
        style={{
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          transform: `translate(${mouse.x * 10}px, ${mouse.y * 10}px)`
        }}
      />
      {/* Radial fade */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, #0a0a0a 70%)'
        }}
      />
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#chi-siamo", label: "Chi Siamo" },
    { href: "#servizi", label: "Servizi" },
    { href: "#perche-noi", label: "Perché Noi" },
    { href: "#processo", label: "Processo" },
    { href: "#portfolio", label: "Portfolio" },
    { href: "#contatti", label: "Contatti" }
  ];

  const scrollToSection = (href) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav data-testid="main-navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass py-3' : 'py-5'}`}>
      <div className="container-custom flex items-center justify-between">
        <a href="#" className="flex items-center">
          <img 
            src="/images/logo.png" 
            alt="M&M Web Designers" 
            className="h-16 w-auto"
            data-testid="header-logo"
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className="nav-link text-sm font-medium"
            >
              {link.label}
            </button>
          ))}
          <button 
            onClick={() => scrollToSection('#contatti')}
            className="btn-primary text-sm"
            data-testid="nav-cta-button"
          >
            <span>Consulenza Gratuita</span>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden glass mt-2 mx-4 rounded-2xl p-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-left text-white/80 hover:text-[#0ea5e9] transition-colors py-2"
              >
                {link.label}
              </button>
            ))}
            <button 
              onClick={() => scrollToSection('#contatti')}
              className="btn-primary mt-4 text-center"
            >
              <span>Consulenza Gratuita</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [statsRef, statsVisible] = useScrollAnimation();

  return (
    <section 
      className="hero-section relative" 
      data-testid="hero-section"
    >
      <GridBackground />
      <Floating3DElements variant="hero" />
      <Hero3DCube />

      <div className="container-custom relative z-10">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-[#0ea5e9]/10 border border-[#0ea5e9]/30 rounded-full px-4 py-2 mb-8 border-glow-animate">
            <div className="w-2 h-2 bg-[#0ea5e9] rounded-full animate-pulse" />
            <span className="text-sm text-[#0ea5e9]">Milano & Lugano</span>
          </div>

          <h1 
            ref={titleRef}
            className={`text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6 scroll-fade-in ${titleVisible ? 'visible' : ''}`}
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-title"
          >
            Sistemi Digitali{" "}
            <span className="text-shimmer">Progettati su Misura</span>
          </h1>

          <p className={`text-base lg:text-lg text-white/70 max-w-2xl mb-10 leading-relaxed scroll-fade-in ${titleVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
            Soluzioni digitali per aziende e professionisti, veloci, chiare e con supporto continuo. 
            Trasformiamo la tua visione in una presenza online che converte.
          </p>

          <div className={`flex flex-col sm:flex-row gap-4 scroll-fade-in ${titleVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.4s' }}>
            <button 
              className="btn-primary btn-breathe text-base lg:text-lg px-8 py-4 ripple"
              onClick={() => document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-cta-primary"
            >
              <span className="flex items-center gap-2">
                Richiedi la tua demo gratuita
                <ArrowRight size={20} />
              </span>
            </button>
            <button 
              className="btn-secondary text-base lg:text-lg px-8 py-4"
              onClick={() => document.querySelector('#portfolio')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-cta-secondary"
            >
              Scopri i nostri lavori
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-white/10">
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-[#0ea5e9]" style={{ fontFamily: 'Outfit, sans-serif' }}>30+</div>
              <div className="text-sm text-white/50 mt-1">Clienti seguiti</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-[#0ea5e9]" style={{ fontFamily: 'Outfit, sans-serif' }}>72H</div>
              <div className="text-sm text-white/50 mt-1">Consegna rapida</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-[#0ea5e9]" style={{ fontFamily: 'Outfit, sans-serif' }}>7/7</div>
              <div className="text-sm text-white/50 mt-1">Supporto continuo</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Chi Siamo Section
const ChiSiamoSection = () => {
  const team = [
    {
      name: "Manuel",
      role: "Founder & Developer",
      icon: <PenTool size={24} />,
      image: "/images/manuel.png"
    },
    {
      name: "Mirko",
      role: "Client Manager & Sales",
      icon: <Users size={24} />,
      image: "/images/mirko.png"
    },
    {
      name: "Giorgia",
      role: "Client Manager & Sales",
      icon: <Users size={24} />,
      image: "/images/giorgia.png"
    },
    {
      name: "Stefano",
      role: "Developer",
      icon: <PenTool size={24} />,
      image: "/images/stefano.png"
    }
  ];

  const [sectionRef, sectionVisible] = useScrollAnimation();

  return (
    <section id="chi-siamo" className="section-padding relative" data-testid="chi-siamo-section">
      <div className="container-custom">
        <div ref={sectionRef} className={`text-center max-w-3xl mx-auto mb-16 scroll-fade-in ${sectionVisible ? 'visible' : ''}`}>
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            Chi Siamo
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Un team dedicato al{" "}
            <span className="gradient-text">tuo successo digitale</span>
          </h2>
          <p className="text-white/60 text-base lg:text-lg">
            Siamo M&M Web Designers & Visibility Specialist, un team di professionisti che crea 
            siti web e web app professionali, ottimizza schede Google My Business e realizza materiali 
            grafici per aziende e professionisti locali di Milano e Lugano.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 stagger-children">
          {team.map((member, index) => (
            <div 
              key={member.name}
              className={`team-card team-card-3d scroll-scale ${sectionVisible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.2 + index * 0.15}s` }}
              data-testid={`team-card-${member.name.toLowerCase()}`}
            >
              <div className="team-avatar overflow-hidden">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {member.name}
              </h3>
              <div className="inline-flex items-center gap-2 text-[#0ea5e9] text-sm">
                <span className="icon-hover">{member.icon}</span>
                <span>{member.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Servizi Section
const ServiziSection = () => {
  const mainServices = [
    {
      icon: <Globe size={40} />,
      title: "Sviluppo Siti e Web App",
      description: "Siti e web app responsive, ottimizzati SEO e progettati per convertire. Design moderno e performance elevate.",
      features: ["Design Responsive", "SEO Base Incluso", "Tempi Rapidi", "Manutenzione Continuativa"]
    },
    {
      icon: <Layers size={40} />,
      title: "Gestionali Personalizzati",
      description: "Software su misura per automatizzare i processi della tua attività. Prenotazioni, CRM, gestione clienti e molto altro.",
      features: ["Sviluppo Su Misura", "Automazione Processi", "Integrazioni API", "Supporto Dedicato"]
    },
    {
      icon: <MapPin size={40} />,
      title: "Google My Business",
      description: "Ottimizzazione completa della tua scheda GMB per aumentare la visibilità locale e attirare nuovi clienti.",
      features: ["Ottimizzazione scheda", "Creazione contenuti iniziali", "Gestione recensioni iniziali", "Report e consigli personalizzati"]
    }
  ];

  const complementaryService = {
    icon: <FileText size={32} />,
    title: "Materiali di Comunicazione",
    description: "Biglietti da visita, locandine, PDF professionali e menu digitali. Tutto coordinato con la tua brand identity.",
    features: ["Biglietti da Visita", "Locandine & Flyer", "Cartelli Recensioni", "PDF Professionali"]
  };

  const [sectionRef, sectionVisible] = useScrollAnimation();

  return (
    <section id="servizi" className="section-padding bg-[#0a0a0a] relative overflow-hidden" data-testid="servizi-section">
      <Floating3DElements variant="services" />
      <GridBackground />
      <div className="container-custom relative z-10">
        <div ref={sectionRef} className={`text-center max-w-3xl mx-auto mb-16 scroll-fade-in ${sectionVisible ? 'visible' : ''}`}>
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            I Nostri Servizi
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Soluzioni complete per la{" "}
            <span className="gradient-text">tua presenza online</span>
          </h2>
          <p className="text-white/60 text-base lg:text-lg">
            Offriamo servizi integrati per portare la tua attività nel digitale con 
            tempi rapidi, costi chiari e manutenzione continuativa.
          </p>
        </div>

        {/* Main Services */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {mainServices.map((service, index) => (
            <div 
              key={service.title}
              className={`service-card card-3d-hover scroll-slide-${index === 0 ? 'left' : index === 2 ? 'right' : 'left'} ${sectionVisible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.2 + index * 0.15}s` }}
              data-testid={`service-card-${index}`}
            >
              <div className="text-[#0ea5e9] mb-6 service-icon-3d">
                {service.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {service.title}
              </h3>
              <p className="text-white/60 mb-6">
                {service.description}
              </p>
              <ul className="space-y-3">
                {service.features.map((feature, fIndex) => (
                  <li key={feature} className="flex items-center gap-3 text-sm text-white/80">
                    <Check size={16} className="text-[#0ea5e9] icon-hover" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button 
                className="mt-8 w-full btn-secondary text-sm ripple"
                onClick={() => document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Richiedi Preventivo
              </button>
            </div>
          ))}
        </div>

        {/* Complementary Service */}
        <div className={`scroll-fade-in ${sectionVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.6s' }}>
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
              <div className="flex items-center gap-4">
                <div className="text-[#0ea5e9]">
                  {complementaryService.icon}
                </div>
                <div>
                  <span className="text-[#0ea5e9] text-xs font-medium uppercase tracking-wider">Servizio Complementare</span>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {complementaryService.title}
                  </h3>
                </div>
              </div>
              <p className="text-white/60 text-sm lg:flex-1">
                {complementaryService.description}
              </p>
              <div className="flex flex-wrap gap-3">
                {complementaryService.features.map((feature) => (
                  <span key={feature} className="bg-white/5 border border-white/10 px-3 py-1 rounded-full text-xs text-white/70">
                    {feature}
                  </span>
                ))}
              </div>
              <button 
                className="btn-secondary text-sm whitespace-nowrap ripple"
                onClick={() => document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Info
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Perché Noi Section
const PerchéNoiSection = () => {
  const advantages = [
    {
      icon: <Zap size={24} />,
      title: "Velocità di Esecuzione",
      description: "Consegniamo i progetti in tempi record senza compromettere la qualità"
    },
    {
      icon: <Shield size={24} />,
      title: "Trasparenza Totale",
      description: "Preventivi chiari, nessun costo nascosto, comunicazione costante"
    },
    {
      icon: <MapPin size={24} />,
      title: "Esperienza Locale",
      description: "Conosciamo il mercato di Milano e Lugano, le esigenze dei professionisti locali"
    },
    {
      icon: <HeadphonesIcon size={24} />,
      title: "Supporto Diretto",
      description: "Un team dedicato sempre disponibile per assistenza e modifiche"
    }
  ];

  return (
    <section id="perche-noi" className="section-padding" data-testid="perche-noi-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Team photo - LEFT */}
          <div className="relative rounded-2xl overflow-hidden">
            <img 
              src="/images/perche-noi.png"
              alt="Il team M&M al lavoro"
              className="w-full h-[450px] object-cover object-center"
            />
          </div>

          {/* Content - RIGHT */}
          <div>
            <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
              Perché Sceglierci
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Il partner giusto per{" "}
              <span className="gradient-text">il tuo business</span>
            </h2>
            <p className="text-white/60 text-base lg:text-lg mb-8">
              Non siamo solo sviluppatori, siamo partner strategici che comprendono 
              le esigenze delle attività locali e sanno come tradurle in risultati concreti.
            </p>
            
            {/* Advantages grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {advantages.map((adv, index) => (
                <div 
                  key={adv.title}
                  className="flex items-start gap-3"
                  data-testid={`advantage-item-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0 text-[#0ea5e9]">
                    {adv.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {adv.title}
                    </h4>
                    <p className="text-white/50 text-xs leading-relaxed">
                      {adv.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button 
              className="btn-primary"
              onClick={() => document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="perche-noi-cta"
            >
              <span className="flex items-center gap-2">
                Parliamone insieme
                <ArrowRight size={18} />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Processo Section
const ProcessoSection = () => {
  const [sectionRef, sectionVisible] = useScrollAnimation();
  
  const steps = [
    {
      number: "01",
      title: "Analisi Rapida",
      description: "Capiamo le tue esigenze, analizziamo il tuo settore e definiamo insieme obiettivi chiari e raggiungibili.",
      icon: <Search size={24} />
    },
    {
      number: "02",
      title: "Sviluppo Veloce",
      description: "Creiamo il tuo progetto con le migliori tecnologie, mantenendoti aggiornato ad ogni fase del processo.",
      icon: <PenTool size={24} />
    },
    {
      number: "03",
      title: "Crescita e Manutenzione",
      description: "Non ti abbandoniamo dopo la consegna. Offriamo supporto continuo e aggiornamenti per la tua crescita.",
      icon: <Rocket size={24} />
    }
  ];

  return (
    <section id="processo" className="section-padding bg-[#0a0a0a] relative overflow-hidden" data-testid="processo-section">
      <Floating3DElements variant="process" />
      <GridBackground />
      <div className="container-custom relative z-10">
        <div ref={sectionRef} className={`text-center max-w-3xl mx-auto mb-16 scroll-fade-in ${sectionVisible ? 'visible' : ''}`}>
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            Come Lavoriamo
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Un processo{" "}
            <span className="gradient-text">semplice e efficace</span>
          </h2>
          <p className="text-white/60 text-base lg:text-lg">
            Tre step per trasformare la tua idea in realtà, con tempi certi e risultati garantiti.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connector line - desktop only with 3D animation */}
          <div className={`hidden md:block absolute top-[60px] left-[16%] right-[16%] h-[3px] process-3d-line ${sectionVisible ? 'visible' : ''}`} />
          
          {steps.map((step, index) => (
            <div 
              key={step.number}
              className={`process-step text-center card-3d-hover scroll-scale ${sectionVisible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.3 + index * 0.2}s` }}
              data-testid={`process-step-${index}`}
            >
              <div className="process-number mx-auto relative z-10 hero-3d-glow">
                {step.number}
              </div>
              <div className="text-[#0ea5e9] flex justify-center my-4">
                <span className="icon-hover service-icon-3d">{step.icon}</span>
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {step.title}
              </h3>
              <p className="text-white/60 text-sm">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Study Section (ex Portfolio)
const CaseStudySection = () => {
  const [sectionRef, sectionVisible] = useScrollAnimation();
  
  const caseStudies = [
    {
      title: "Paul Cars - Concessionaria Auto",
      category: "Sito Web",
      domain: "paul-cars.it",
      url: "https://paul-cars.it",
      image: "/images/case-paul-cars.webp",
      problem: "Concessionaria auto senza presenza online che perdeva potenziali clienti che cercavano veicoli su Google nella zona.",
      solution: "Sviluppo sito web professionale con catalogo auto, schede veicoli dettagliate, gallery fotografica e form di contatto per richieste preventivo.",
      result: "Prima presenza digitale dell'azienda con aumento significativo delle richieste di informazioni e visite."
    },
    {
      title: "Simone Perez - Consulente Immobiliare",
      category: "Sito Web",
      domain: "simoneperez.ch",
      url: "https://simoneperez.ch",
      image: "/images/case-simoneperez.webp",
      problem: "Il consulente immobiliare in Ticino necessitava di un sito professionale per presentare i servizi di compravendita immobili.",
      solution: "Sito vetrina elegante con presentazione servizi, portfolio immobili e form di contatto ottimizzato per lead generation.",
      result: "Presenza online professionale che riflette la qualità del servizio offerto, aumento delle richieste di consulenza."
    },
    {
      title: "AeroDeals - Marketplace Aviazione",
      category: "Sito Web",
      domain: "aerodeals.it",
      url: "https://aerodeals.it",
      image: "/images/case-aerodeals.webp",
      problem: "Piattaforma di compravendita ultraleggeri e aerei che necessitava di un sito moderno e funzionale per il mercato italiano ed europeo.",
      solution: "Sviluppo marketplace con sistema di annunci, ricerca avanzata e interfaccia user-friendly per acquirenti e venditori.",
      result: "Piattaforma di riferimento per il settore aviazione in Italia, con traffico organico in crescita costante."
    }
  ];

  return (
    <section id="portfolio" className="section-padding relative overflow-hidden" data-testid="portfolio-section">
      <Floating3DElements variant="portfolio" />
      <GridBackground />
      <div className="container-custom relative z-10">
        <div ref={sectionRef} className={`text-center max-w-3xl mx-auto mb-12 scroll-fade-in ${sectionVisible ? 'visible' : ''}`}>
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            Case Study
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Risultati{" "}
            <span className="gradient-text">concreti per i nostri clienti</span>
          </h2>
          <p className="text-white/60 text-base lg:text-lg">
            Ogni progetto parte da un problema reale e arriva a risultati misurabili. 
            Ecco come abbiamo aiutato alcune attività come la tua.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {caseStudies.map((study, index) => (
            <div 
              key={study.title}
              className={`bg-[#111] border border-white/10 rounded-2xl overflow-hidden hover:border-[#0ea5e9]/50 transition-all duration-300 scroll-scale ${sectionVisible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.1 + index * 0.1}s` }}
              data-testid={`case-study-${index}`}
            >
              {/* Screenshot Image - Clickable */}
              <a 
                href={study.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block relative overflow-hidden group"
              >
                <img 
                  src={study.image} 
                  alt={`Screenshot ${study.title}`}
                  className="w-full h-48 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent opacity-60" />
                <div className="absolute inset-0 bg-[#0ea5e9]/0 group-hover:bg-[#0ea5e9]/10 transition-colors duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm font-medium">
                    Visita il sito →
                  </span>
                </div>
              </a>
              
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#0ea5e9]/20 flex items-center justify-center">
                    <Globe size={20} className="text-[#0ea5e9]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {study.title}
                    </h3>
                    <span className="text-[#0ea5e9] text-xs">{study.category}</span>
                    <span className="text-white/40 text-xs ml-2">• {study.domain}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X size={10} className="text-red-400" />
                    </div>
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Problema</span>
                      <p className="text-white/70 text-xs mt-1 leading-relaxed">{study.problem}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#0ea5e9]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Zap size={10} className="text-[#0ea5e9]" />
                    </div>
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Soluzione</span>
                      <p className="text-white/70 text-xs mt-1 leading-relaxed">{study.solution}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={10} className="text-green-400" />
                    </div>
                    <div>
                      <span className="text-white/40 text-xs uppercase tracking-wider">Risultato</span>
                      <p className="text-white/70 text-xs mt-1 leading-relaxed">{study.result}</p>
                    </div>
                  </div>
                </div>
                
                <button 
                  className="mt-5 w-full btn-secondary text-sm py-2 ripple"
                  onClick={() => document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Voglio risultati simili
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [sectionRef, sectionVisible] = useScrollAnimation();
  const scrollRef = useRef(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  const testimonials = [
    {
      name: "Simonangelo Grasso",
      text: "Grazie a Manuel e a tutto il suo team, ho finalmente potuto aumentare considerevolmente la mia visibilità con il mio progetto riguardante un sito web di navigazione aerea. Molto attenti alle esigenze del cliente e soprattutto abili, proattivi e veloci nel cercare una soluzione e consegnare il progetto nel più breve tempo possibile. Molto raccomandato!",
      rating: 5
    },
    {
      name: "Diana Piano",
      text: "Professionalità, esperienza e velocità. Ci hanno aiutato tanto in termini di visibilità con la scheda google e sito web. Ragazzi onesti e disponibili. Consigliati!",
      rating: 5
    },
    {
      name: "Vanessa Laureana",
      text: "Mi sono rivolta a questa web agency per lo sviluppo di materiali di comunicazione ed aumentare la media delle recensioni con l'obiettivo di migliorare la visibilità locale del mio ambulatorio.",
      rating: 5
    },
    {
      name: "Luigi B.",
      text: "Ottima esperienza, si sono occupati di tutta la visibilità del mio coffee pub: dal sito web ai bigliettini da visita e qrcode, nonché tutta la parte di Google.",
      rating: 5
    },
    {
      name: "Mattia Maron",
      text: "Come artigiano, il mio lavoro parla da sé ma non sapevo come mostrarlo online. Ora ho un sito che riflette la mia immagine e la qualità delle mie creazioni. I clienti mi contattano già sapendo cosa aspettarsi.",
      rating: 5
    },
    {
      name: "Rebecca Cosentino",
      text: "Per noi è stata un'esperienza positiva e concreta. Li consigliamo a chi cerca un supporto serio per migliorare la propria presenza online.",
      rating: 5
    },
    {
      name: "Christian Vocca",
      text: "Avevo bisogno di un sito veloce e chiaro, che mi desse visibilità e mi permettesse di avvicinare un buon numero di clienti nuovi.",
      rating: 5
    }
  ];

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -350, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 350, behavior: 'smooth' });
    }
  };

  const MAX_LENGTH = 150;

  return (
    <section className="section-padding bg-[#0a0a0a] relative overflow-hidden" data-testid="testimonials-section">
      <div className="container-custom relative z-10">
        <div ref={sectionRef} className={`text-center max-w-3xl mx-auto mb-12 scroll-fade-in ${sectionVisible ? 'visible' : ''}`}>
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            Testimonianze
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Cosa dicono{" "}
            <span className="gradient-text">i nostri clienti</span>
          </h2>
        </div>

        <div className="relative">
          {/* Scroll buttons */}
          <button 
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0ea5e9] text-white flex items-center justify-center hover:bg-[#0284c7] transition-colors hidden md:flex"
            style={{ marginLeft: '-20px' }}
          >
            <ChevronRight size={20} className="rotate-180" />
          </button>
          <button 
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0ea5e9] text-white flex items-center justify-center hover:bg-[#0284c7] transition-colors hidden md:flex"
            style={{ marginRight: '-20px' }}
          >
            <ChevronRight size={20} />
          </button>

          {/* Testimonials carousel */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial, index) => {
              const isExpanded = expandedIndex === index;
              const needsTruncate = testimonial.text.length > MAX_LENGTH;
              const displayText = isExpanded || !needsTruncate 
                ? testimonial.text 
                : testimonial.text.substring(0, MAX_LENGTH) + '...';
              
              return (
                <div 
                  key={index}
                  className={`flex-shrink-0 w-[320px] md:w-[380px] bg-[#111] border border-white/10 rounded-2xl p-6 snap-start scroll-scale flex flex-col ${sectionVisible ? 'visible' : ''}`}
                  style={{ transitionDelay: `${0.1 + index * 0.1}s`, minHeight: '280px' }}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="text-[#0ea5e9] fill-[#0ea5e9]" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <div className="flex-grow">
                    <p className="text-white/70 text-sm leading-relaxed">
                      "{displayText}"
                    </p>
                    {needsTruncate && (
                      <button 
                        onClick={() => setExpandedIndex(isExpanded ? null : index)}
                        className="text-[#0ea5e9] text-sm mt-2 hover:underline"
                      >
                        {isExpanded ? 'Mostra meno' : 'Leggi di più'}
                      </button>
                    )}
                  </div>
                  
                  {/* Author */}
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/5">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {testimonial.name.split(' ')[0][0]}{testimonial.name.split(' ')[1]?.[0] || ''}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{testimonial.name}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Quiz Section
const QuizSection = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    project_type: '',
    pages_count: '',
    features: [],
    timeline: '',
    budget_range: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const questions = [
    {
      key: 'project_type',
      title: 'Che tipo di progetto ti serve?',
      options: [
        { value: 'landing', label: 'Landing Page', desc: 'Pagina singola promozionale' },
        { value: 'website', label: 'Sito Web / Web App', desc: 'Sito multi-pagina o applicazione web' },
        { value: 'ecommerce', label: 'E-commerce', desc: 'Negozio online con pagamenti' },
        { value: 'webapp', label: 'Web App', desc: 'Applicazione web personalizzata' }
      ]
    },
    {
      key: 'pages_count',
      title: 'Quante pagine ti servono?',
      options: [
        { value: '1-3', label: '1-3 pagine', desc: 'Essenziale' },
        { value: '4-7', label: '4-7 pagine', desc: 'Standard' },
        { value: '8-15', label: '8-15 pagine', desc: 'Completo' },
        { value: '15+', label: '15+ pagine', desc: 'Enterprise' }
      ]
    },
    {
      key: 'features',
      title: 'Quali funzionalità ti interessano?',
      multiple: true,
      options: [
        { value: 'seo', label: 'Ottimizzazione SEO' },
        { value: 'analytics', label: 'Analytics e Report' },
        { value: 'forms', label: 'Form Contatti' },
        { value: 'gmb', label: 'Google My Business' },
        { value: 'social', label: 'Integrazione Social' },
        { value: 'blog', label: 'Blog / News' },
        { value: 'multilang', label: 'Multilingua' },
        { value: 'booking', label: 'Sistema Prenotazioni' }
      ]
    },
    {
      key: 'timeline',
      title: 'Quando ti serve il progetto?',
      options: [
        { value: 'urgent', label: 'Urgente', desc: 'Entro 1 settimana' },
        { value: 'normal', label: 'Normale', desc: '2-4 settimane' },
        { value: 'flexible', label: 'Flessibile', desc: 'Nessuna fretta' }
      ]
    },
    {
      key: 'budget_range',
      title: 'Qual è il tuo budget indicativo?',
      options: [
        { value: 'small', label: '€500 - €1.000' },
        { value: 'medium', label: '€1.000 - €2.500' },
        { value: 'large', label: '€2.500 - €5.000' },
        { value: 'enterprise', label: '€5.000+' }
      ]
    }
  ];

  const handleSelect = (key, value, multiple = false) => {
    if (multiple) {
      const current = answers[key] || [];
      const updated = current.includes(value) 
        ? current.filter(v => v !== value)
        : [...current, value];
      setAnswers({ ...answers, [key]: updated });
    } else {
      setAnswers({ ...answers, [key]: value });
      setTimeout(() => setStep(step + 1), 300);
    }
  };

  const handleSubmit = async () => {
    if (!answers.contact_name || !answers.contact_email) {
      toast.error('Inserisci nome e email per ricevere il preventivo');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/quiz`, answers);
      setEstimate(response.data.estimated_price);
      toast.success('Preventivo calcolato con successo!');
    } catch (error) {
      toast.error('Errore nel calcolo del preventivo');
    }
    setLoading(false);
  };

  const progress = ((step + 1) / (questions.length + 1)) * 100;

  return (
    <section id="preventivo" className="section-padding bg-[#0a0a0a]" data-testid="quiz-section">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
            Preventivo Rapido
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Calcola il tuo{" "}
            <span className="gradient-text">preventivo in 2 minuti</span>
          </h2>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="quiz-container">
            <Progress value={progress} className="mb-8 h-2" />

            {estimate ? (
              <div className="text-center" data-testid="quiz-result">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0ea5e9]/20 flex items-center justify-center">
                  <Check size={40} className="text-[#0ea5e9]" />
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Il tuo preventivo stimato
                </h3>
                <div className="text-4xl font-bold gradient-text mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {estimate}
                </div>
                <p className="text-white/60 mb-8">
                  Ti contatteremo entro 24 ore per una consulenza gratuita e un preventivo dettagliato.
                </p>
                <button 
                  className="btn-primary"
                  onClick={() => {
                    setStep(0);
                    setEstimate(null);
                    setAnswers({
                      project_type: '',
                      pages_count: '',
                      features: [],
                      timeline: '',
                      budget_range: '',
                      contact_name: '',
                      contact_email: '',
                      contact_phone: ''
                    });
                  }}
                >
                  <span>Calcola un nuovo preventivo</span>
                </button>
              </div>
            ) : step < questions.length ? (
              <div data-testid={`quiz-step-${step}`}>
                <h3 className="text-xl font-bold mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {questions[step].title}
                </h3>
                <div className="grid gap-3">
                  {questions[step].options.map((option) => (
                    <div
                      key={option.value}
                      className={`quiz-option ${
                        questions[step].multiple 
                          ? answers[questions[step].key]?.includes(option.value) ? 'selected' : ''
                          : answers[questions[step].key] === option.value ? 'selected' : ''
                      }`}
                      onClick={() => handleSelect(questions[step].key, option.value, questions[step].multiple)}
                      data-testid={`quiz-option-${option.value}`}
                    >
                      <div className="quiz-option-icon">
                        {(questions[step].multiple 
                          ? answers[questions[step].key]?.includes(option.value)
                          : answers[questions[step].key] === option.value) && (
                          <Check size={14} className="text-white" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        {option.desc && <div className="text-sm text-white/50">{option.desc}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {questions[step].multiple && (
                  <button 
                    className="btn-primary w-full mt-6"
                    onClick={() => setStep(step + 1)}
                  >
                    <span>Continua</span>
                  </button>
                )}
                {step > 0 && (
                  <button 
                    className="text-white/50 text-sm mt-4 hover:text-white transition-colors w-full text-center"
                    onClick={() => setStep(step - 1)}
                  >
                    Torna indietro
                  </button>
                )}
              </div>
            ) : (
              <div data-testid="quiz-contact-form">
                <h3 className="text-xl font-bold mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Ultimo step: i tuoi contatti
                </h3>
                <div className="space-y-4">
                  <Input
                    placeholder="Il tuo nome *"
                    value={answers.contact_name}
                    onChange={(e) => setAnswers({ ...answers, contact_name: e.target.value })}
                    className="form-input"
                    data-testid="quiz-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="La tua email *"
                    value={answers.contact_email}
                    onChange={(e) => setAnswers({ ...answers, contact_email: e.target.value })}
                    className="form-input"
                    data-testid="quiz-email-input"
                  />
                  <Input
                    type="tel"
                    placeholder="Telefono (opzionale)"
                    value={answers.contact_phone}
                    onChange={(e) => setAnswers({ ...answers, contact_phone: e.target.value })}
                    className="form-input"
                    data-testid="quiz-phone-input"
                  />
                  <button 
                    className="btn-primary w-full mt-4"
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="quiz-submit-button"
                  >
                    <span>{loading ? 'Calcolo in corso...' : 'Calcola Preventivo'}</span>
                  </button>
                  <button 
                    className="text-white/50 text-sm hover:text-white transition-colors w-full text-center"
                    onClick={() => setStep(step - 1)}
                  >
                    Torna indietro
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_interest: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, formData);
      toast.success('Messaggio inviato con successo! Ti ricontatteremo presto.');
      setFormData({ name: '', email: '', phone: '', message: '', service_interest: '' });
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio');
    }
    setLoading(false);
  };

  return (
    <section id="contatti" className="section-padding" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="text-[#0ea5e9] text-sm font-medium tracking-wider uppercase mb-4 block">
              Contattaci
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Prenota la tua{" "}
              <span className="gradient-text">call gratuita</span>
            </h2>
            <p className="text-white/60 text-base lg:text-lg mb-8">
              Rispondiamo entro 24 ore. Raccontaci il tuo progetto e 
              troveremo insieme la soluzione perfetta per te.
            </p>

            <div className="space-y-6">
              <a 
                href="mailto:info@mm-webdesigners.it" 
                className="flex items-center gap-4 text-white/80 hover:text-[#0ea5e9] transition-colors"
                data-testid="contact-email-link"
              >
                <div className="w-12 h-12 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center">
                  <Mail size={20} className="text-[#0ea5e9]" />
                </div>
                <span>info@mm-webdesigners.it</span>
              </a>
              <a 
                href="tel:+393793892910" 
                className="flex items-center gap-4 text-white/80 hover:text-[#0ea5e9] transition-colors"
                data-testid="contact-phone-link"
              >
                <div className="w-12 h-12 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center">
                  <Phone size={20} className="text-[#0ea5e9]" />
                </div>
                <span>+39 379 389 2910</span>
              </a>
              <a 
                href="https://wa.me/393793892910" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-white/80 hover:text-[#25D366] transition-colors"
                data-testid="contact-whatsapp-link"
              >
                <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span>Chatta su WhatsApp</span>
              </a>
              <div className="flex items-center gap-4 text-white/80">
                <div className="w-12 h-12 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center">
                  <MapPin size={20} className="text-[#0ea5e9]" />
                </div>
                <span>Milano & Lugano</span>
              </div>
            </div>
          </div>

          <div className="contact-form">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-4">
                <Input
                  placeholder="Il tuo nome *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  data-testid="contact-name-input"
                />
                <Input
                  type="email"
                  placeholder="La tua email *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  data-testid="contact-email-input"
                />
                <Input
                  type="tel"
                  placeholder="Telefono (opzionale)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                  data-testid="contact-phone-input"
                />
                <Textarea
                  placeholder="Descrivi il tuo progetto *"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="form-input min-h-[150px] resize-none"
                  data-testid="contact-message-input"
                />
                <Button 
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="contact-submit-button"
                >
                  <span className="flex items-center justify-center gap-2">
                    {loading ? 'Invio in corso...' : 'Invia Messaggio'}
                    <Send size={18} />
                  </span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Privacy Policy Modal Component
const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Privacy Policy
          </DialogTitle>
        </DialogHeader>
        <div className="text-white/70 text-sm space-y-4 pr-4">
          <h3 className="text-[#0ea5e9] font-semibold text-base">
            Informativa resa ai sensi degli art. 13-14 del GDPR (General Data Protection Regulation) 2016/679
          </h3>
          
          <p>
            La informiamo che, per l'instaurazione e l'esecuzione di rapporti contrattuali la nostra organizzazione anche tramite il presente sito potrebbe entrare in possesso di Suoi dati, acquisiti anche verbalmente, direttamente o tramite terzi, qualificati come personali dal Regolamento Europeo 2016/679 (GDPR) e dal D. Lgs. 196/2003, come modificato dal D. Lgs. 101/2018.
          </p>
          
          <p>
            Secondo la normativa indicata, tale trattamento sarà improntato ai principi di correttezza, liceità, trasparenza e di tutela della Sua riservatezza e dei Suoi diritti.
          </p>
          
          <h4 className="text-white font-semibold mt-4">Natura dei dati trattati</h4>
          <p>
            Trattiamo i Suoi dati anagrafici, fiscali, di contatto e di natura economica necessari allo svolgimento dei rapporti contrattuali, in essere o futuri da Lei comunicati in occasione della conclusione ed esecuzione di contratti, ovvero in virtù di rapporti e trattative pre-negoziali. Non siamo in possesso di Suoi dati qualificabili come particolari o di natura giudiziaria (art. 9 e 10 del GDPR).
          </p>
          
          <p>
            I Servizi del Titolare non sono destinati a minori di 18 anni e il Titolare non raccoglie intenzionalmente informazioni personali riferite ai minori. Ove il soggetto che conferisce i dati sia minorenne, tale trattamento è lecito soltanto se e nella misura in cui tale consenso è prestato o autorizzato dal titolare della responsabilità genitoriale per il quale sono acquisiti i dati identificativi e copia dei documenti di riconoscimento. Differentemente, nel caso in cui informazioni su minori fossero involontariamente registrate o raccolte, il Titolare le cancellerà tempestivamente.
          </p>
          
          <h4 className="text-white font-semibold mt-4">Finalità del trattamento, durata e base giuridica</h4>
          <p>
            I Suoi dati vengono trattati per tutta la durata del rapporto contrattuale ed anche successivamente, senza il Suo consenso espresso ex art. 6 lett. b e f GDPR, solo ed esclusivamente per le seguenti finalità: adempiere agli obblighi precontrattuali, contrattuali e fiscali; adempiere agli obblighi previsti dalla legge, da un regolamento, dalla normativa comunitaria o da un ordine dell'Autorità; esercitare i diritti del Titolare, ad esempio il diritto di difesa in giudizio. Il Titolare tratterà i dati personali per il tempo necessario per adempiere alle finalità di cui sopra e comunque per non oltre 10 anni dalla cessazione del rapporto.
          </p>
          
          <p>
            Le segnaliamo che se è già cliente, potremo inviarLe comunicazioni commerciali relative a servizi e prodotti del Titolare analoghi a quelli di cui ha già usufruito, salvo Suo dissenso (artt. 130 D.lgs. 196/2003 e 21 n.2 GDPR).
          </p>
          
          <h4 className="text-white font-semibold mt-4">Obbligo o facoltà di conferire i dati</h4>
          <p>
            Per quanto concerne i dati che siamo obbligati a conoscere, al fine di adempiere agli obblighi previsti da leggi, il loro mancato conferimento da parte Sua comporta l'impossibilità di instaurare o proseguire il rapporto, nei limiti in cui tali dati sono necessari all'esecuzione dello stesso.
          </p>
          
          <h4 className="text-white font-semibold mt-4">Ambito di conoscenza dei Suoi dati</h4>
          <p>
            Le seguenti categorie di soggetti possono venire a conoscenza dei Suoi dati, in qualità di responsabili o soggetti autorizzati al trattamento, nominati dalla scrivente società, titolare del trattamento: dirigenti, amministratori e sindaci; uffici di segreteria interni; addetti alla contabilità ed alla fatturazione; addetti alla commercializzazione dei servizi; terzi soggetti (ad esempio, provider per la gestione e manutenzione del sito web, fornitori, istituti di credito, studi professionali, etc.) che svolgono attività in outsourcing per conto del Titolare, nella loro qualità di responsabili esterni del trattamento; Organismi di vigilanza, Autorità giudiziarie, nonché a tutti gli altri soggetti ai quali la comunicazione sia obbligatoria per legge per l'espletamento delle finalità indicate.
          </p>
          
          <h4 className="text-white font-semibold mt-4">Comunicazione e diffusione</h4>
          <p>
            I Suoi dati non verranno da noi diffusi a soggetti indeterminati mediante la loro messa a disposizione o consultazione. I Suoi dati potranno da noi essere comunicati, per quanto di loro rispettiva e specifica competenza, ad Enti ed in generale ad ogni soggetto pubblico o privato rispetto al quale vi sia per noi obbligo (o facoltà riconosciuta da norme di legge o di normativa secondaria o comunitaria) o necessità di comunicazione, nonché a soggetti nostri consulenti, nei limiti necessari per svolgere il loro incarico presso la nostra organizzazione, previo nostra lettera di incarico che imponga il dovere di riservatezza e sicurezza.
          </p>
          
          <h4 className="text-white font-semibold mt-4">I Suoi diritti</h4>
          <p>
            Nella Sua qualità di interessato, avete i diritti di cui all'art. 15 GDPR e precisamente i diritti di:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>ottenere la conferma dell'esistenza o meno di dati personali che La riguardano, anche se non ancora registrati, e la loro comunicazione in forma intelligibile;</li>
            <li>ottenere l'indicazione: a) dell'origine dei dati personali; b) delle finalità e modalità del trattamento; c) della logica applicata in caso di trattamento effettuato con l'ausilio di strumenti elettronici; d) degli estremi identificativi del titolare, dei responsabili e del rappresentante designato ai sensi dell'art. 3, comma 1, GDPR; e) dei soggetti o delle categorie di soggetti ai quali i dati personali possono essere comunicati o che possono venirne a conoscenza in qualità di rappresentante designato nel territorio dello Stato, di responsabili o incaricati;</li>
            <li>ottenere: a) l'aggiornamento, la rettificazione ovvero l'integrazione dei dati; b) la cancellazione, la trasformazione in forma anonima o il blocco dei dati trattati in violazione di legge, compresi quelli di cui non è necessaria la conservazione in relazione agli scopi per i quali i dati sono stati raccolti o successivamente trattati; c) l'attestazione che le operazioni di cui alle lettere a) e b) sono state portate a conoscenza, anche per quanto riguarda il loro contenuto, di coloro ai quali i dati sono stati comunicati o diffusi, eccettuato il caso in cui tale adempimento si rivela impossibile o comporta un impiego di mezzi manifestamente sproporzionato rispetto al diritto tutelato;</li>
            <li>opporsi, in tutto o in parte: a) per motivi legittimi al trattamento dei dati personali che La riguardano, ancorché pertinenti allo scopo della raccolta; b) al trattamento di dati personali che La riguardano a fini di invio di materiale pubblicitario o di vendita diretta o per il compimento di ricerche di mercato o di comunicazione commerciale, mediante l'uso di sistemi automatizzati di chiamata senza l'intervento di un operatore, mediante email e/o mediante modalità di marketing tradizionali quali telefono e/o posta cartacea.</li>
          </ul>
          <p>
            Ove applicabili, avrete altresì i diritti di cui agli artt. 16-21 GDPR (Diritto di rettifica, diritto all'oblio, diritto di limitazione di trattamento, diritto alla portabilità dei dati, diritto di opposizione), nonché il diritto di reclamo all'Autorità Garante.
          </p>
          <p>
            In qualsiasi momento potrete ottenere conferma dell'esistenza o meno di dati personali che La riguardano e la comunicazione di tali dati e delle finalità su cui si basa il trattamento. Inoltre, potrete ottenere la cancellazione, la trasformazione in forma anonima o il blocco dei dati trattati in violazione di legge, nonché l'aggiornamento, la rettificazione o, qualora sussista un Suo interesse in merito, l'integrazione dei dati. Potrà opporsi, per motivi legittimi, al trattamento stesso.
          </p>
          <p>
            Le chiediamo cortesemente di segnalare tempestivamente all'Ufficio di riferimento della società ogni eventuale variazione dei Suoi dati personali in modo da poter ottemperare alla normativa vigente, che richiede che i dati raccolti siano esatti e, quindi, aggiornati.
          </p>
          
          <h4 className="text-white font-semibold mt-4">Titolare e responsabile del trattamento</h4>
          <p>
            Titolare del trattamento è <strong className="text-white">M&M</strong>
          </p>
          <p>
            Raggiungibile all'indirizzo mail: <a href="mailto:info@mm-webdesigners.it" className="text-[#0ea5e9] hover:underline">info@mm-webdesigners.it</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Terms and Conditions Modal Component
const TermsConditionsModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Termini e Condizioni
          </DialogTitle>
        </DialogHeader>
        <div className="text-white/70 text-sm space-y-4 pr-4">
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-2">1 | Ambito di applicazione</h3>
          <p>
            <strong className="text-white">1.1</strong> I presenti Termini e Condizioni disciplinano la fornitura dei servizi offerti da M&M Web Designers & Visibility Specialists (sviluppo siti web e web app, creazione e gestione schede Google My Business, materiali di comunicazione grafica) a clienti privati e aziende.
          </p>
          <p>
            <strong className="text-white">1.2</strong> L'accettazione di un preventivo o il pagamento di una fattura comporta l'accettazione integrale dei presenti Termini.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">2 | Oggetto dei servizi</h3>
          <p>
            <strong className="text-white">2.1</strong> M&M realizza siti web, web app, schede Google My Business e materiali grafici sulla base delle informazioni e dei contenuti forniti dal cliente.
          </p>
          <p>
            <strong className="text-white">2.2</strong> Il cliente è responsabile della correttezza dei contenuti (testi, immagini, loghi, dati legali).
          </p>
          <p>
            <strong className="text-white">2.3</strong> Il servizio include solo le funzionalità indicate nel preventivo approvato.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">3 | Tempistiche</h3>
          <p>
            <strong className="text-white">3.1</strong> Le tempistiche indicate sono stimate e dipendono dalla consegna puntuale dei materiali da parte del cliente.
          </p>
          <p>
            <strong className="text-white">3.2</strong> Ritardi nella consegna dei contenuti possono posticipare la data di consegna del progetto.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">4 | Revisioni</h3>
          <p>
            <strong className="text-white">4.1</strong> Ogni progetto include fino a 2 cicli di revisioni sui contenuti se non concordati su preventivo.
          </p>
          <p>
            <strong className="text-white">4.2</strong> Modifiche aggiuntive o richieste fuori preventivo saranno fatturate a parte secondo tariffa oraria o nuovo preventivo.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">5 | Prezzi e Pagamenti</h3>
          <p>
            <strong className="text-white">5.1</strong> I prezzi indicati nei preventivi sono esenti di IVA (contribuente in regime forfettario) salvo diversa indicazione.
          </p>
          <p>
            <strong className="text-white">5.2</strong> Modalità standard (salvo diversi accordi indicati su preventivo):
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>50% anticipo</li>
            <li>50% alla consegna prima della pubblicazione</li>
          </ul>
          <p>
            <strong className="text-white">5.3</strong> In caso di mancato pagamento M&M può sospendere il progetto o il servizio.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">6 | Costi esterni</h3>
          <p>Sono a carico del cliente:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>dominio</li>
            <li>hosting</li>
            <li>plugin aggiuntivi</li>
            <li>licenze immagini o font</li>
            <li>campagne pubblicitarie</li>
          </ul>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">7 | Manutenzione</h3>
          <p>
            <strong className="text-white">7.1</strong> La manutenzione del sito non è inclusa salvo contratto annuale.
          </p>
          <p>
            <strong className="text-white">7.2</strong> M&M offre pacchetti di manutenzione opzionali per aggiornamenti, backup e modifiche.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">8 | Responsabilità</h3>
          <p>
            <strong className="text-white">8.1</strong> M&M non garantisce risultati commerciali o posizionamenti SEO specifici.
          </p>
          <p>
            <strong className="text-white">8.2</strong> M&M non è responsabile per:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>blocchi hosting</li>
            <li>problemi server</li>
            <li>modifiche fatte dal cliente o terzi</li>
            <li>contenuti illegali forniti dal cliente</li>
          </ul>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">9 | Diritti e proprietà</h3>
          <p>
            <strong className="text-white">9.1</strong> Dopo saldo completo il cliente diventa proprietario del sito e dei materiali realizzati.
          </p>
          <p>
            <strong className="text-white">9.2</strong> M&M può mostrare il progetto nel portfolio salvo richiesta scritta contraria.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">10 | Recesso</h3>
          <p>
            <strong className="text-white">10.1</strong> In caso di annullamento dopo l'avvio del progetto, l'anticipo non è rimborsabile.
          </p>
          <p>
            <strong className="text-white">10.2</strong> Il lavoro già svolto sarà comunque fatturato.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">11 | Foro competente</h3>
          <p>
            Per ogni controversia è competente il <strong className="text-white">Foro di Milano</strong>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Footer Section
const FooterSection = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Iscrizione completata!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email già registrata');
      } else {
        toast.error('Errore nell\'iscrizione');
      }
    }
    setLoading(false);
  };

  return (
    <footer className="footer section-padding" data-testid="footer-section">
      <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <TermsConditionsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <div className="container-custom">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <a href="#" className="flex items-center mb-4">
              <img 
                src="/images/logo.png" 
                alt="M&M Web Designers" 
                className="h-16 w-auto"
                data-testid="footer-logo"
              />
            </a>
            <p className="text-white/50 text-sm">
              Web Designers & Visibility Specialists
              <br />
              Milano & Lugano
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Servizi</h4>
            <ul className="space-y-3 text-sm text-white/50">
              <li><a href="#servizi" className="hover:text-[#0ea5e9] transition-colors">Sviluppo Siti e Web App</a></li>
              <li><a href="#servizi" className="hover:text-[#0ea5e9] transition-colors">Google My Business</a></li>
              <li><a href="#servizi" className="hover:text-[#0ea5e9] transition-colors">Materiali Grafici</a></li>
              <li><a href="#contatti" className="hover:text-[#0ea5e9] transition-colors">Contattaci</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Link Utili</h4>
            <ul className="space-y-3 text-sm text-white/50">
              <li><a href="#chi-siamo" className="hover:text-[#0ea5e9] transition-colors">Chi Siamo</a></li>
              <li><a href="#portfolio" className="hover:text-[#0ea5e9] transition-colors">Portfolio</a></li>
              <li><a href="#processo" className="hover:text-[#0ea5e9] transition-colors">Come Lavoriamo</a></li>
              <li><a href="#contatti" className="hover:text-[#0ea5e9] transition-colors">Contatti</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Newsletter</h4>
            <p className="text-white/50 text-sm mb-4">
              Ricevi consigli e novità sul web marketing locale.
            </p>
            <form onSubmit={handleNewsletter} className="flex gap-2" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="La tua email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input flex-1 py-2"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit" 
                className="bg-[#0ea5e9] hover:bg-[#0284c7] px-4"
                disabled={loading}
                data-testid="newsletter-submit-button"
              >
                <Send size={18} />
              </Button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            Copyright © 2026 M&M | Powered by MGB Aviation | P. IVA IT13672210963
          </p>
          <div className="flex items-center gap-6">
            <a 
              href="https://www.instagram.com/mm_webdesigners.it" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/40 hover:text-[#E4405F] transition-colors"
              data-testid="instagram-link"
            >
              <Instagram size={20} />
            </a>
            <div className="flex gap-6 text-sm text-white/40">
              <button 
                onClick={() => setShowPrivacy(true)} 
                className="hover:text-[#0ea5e9] transition-colors"
                data-testid="privacy-policy-link"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowTerms(true)} 
                className="hover:text-[#0ea5e9] transition-colors"
                data-testid="terms-conditions-link"
              >
                Termini e Condizioni
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <MouseProvider>
      <div className="App bg-[#0a0a0a] min-h-screen">
        <ScrollProgress />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#111',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)'
            }
          }}
        />
        <Navigation />
        <HeroSection />
        <ChiSiamoSection />
        <LaptopOpeningSection />
        <ServiziSection />
        <BrowserSearchSection />
        <PerchéNoiSection />
        <ProcessoSection />
        <WebsiteReadySection />
        <CaseStudySection />
        <TestimonialsSection />
        <ContactSection />
        <FooterSection />
      </div>
    </MouseProvider>
  );
}

export default App;
